import { useMemo } from 'react';
import { usePreferences, useSetPreference } from '../../data/hooks';

/**
 * WHICH VIEW A LIST REOPENS ON, remembered per PERSON and per object
 * (`view-persistence.md` V1, V4, V5).
 *
 * The owner's finding on 2026-09-02 was that every way INTO a list except a reload landed
 * him back on the object's default view: the sidebar's object link, the command menu, a
 * dashboard link and a fresh tab all navigate to a bare `/objects/<id>`, and a bare URL
 * means "the default view" (D22). A reload was never the problem - it keeps `?view=` in
 * the address bar.
 *
 * THE STORE IS THE PER-USER PREFERENCE STORE that already exists (dashboard.md D33):
 * `GET /api/auth/me/preferences` and `PUT /api/auth/me/preferences/:key`, read here
 * through `usePreferences` and written through `useSetPreference`, exactly as the
 * dashboard's hidden panels are. No new table, no new route, no new migration - and the
 * memory follows the person to their second browser and their phone, which is what the
 * owner decided on 2026-09-05.
 *
 * ONE INTERFACE, ONE IMPLEMENTATION, ONE MODULE. `RecordListPage` names neither the
 * preference store nor any other: it asks `useLastViewStorage()` for whichever store is
 * bound and calls three methods. A change of store is this file and its tests.
 *
 * AN API KEY HAS NO PREFERENCES, by the route's own decision ("a key is not somebody's
 * dashboard"), and nothing here changes that: the memory belongs to the browser session
 * that is looking at the list. A key-authenticated caller of `/api/query` never comes
 * near this module.
 */

/**
 * THE EXPLICIT UNSAVED LIST, stored as a value of its own (V1).
 *
 * "All records" is a choice somebody made, and a missing key is a choice nobody made - the
 * first reopens the unsaved list, the second opens the default view (V2). One value cannot
 * mean both, which is why this literal exists rather than a stored empty string.
 *
 * It cannot collide with a view id: ids are UUIDv7 (`newId`), so `none` is not one.
 */
export const NO_VIEW = 'none';

/**
 * ONE PREFERENCE KEY PER OBJECT, in the only shape the server accepts.
 *
 * `userPreferenceKeySchema` (`shared/auth.ts`) allows dotted lower-case segments of
 * letters and digits, each STARTING WITH A LETTER, up to 120 characters. A uuid's hyphens
 * are illegal in it and a uuid that starts with a digit cannot start a segment, so the id
 * is stripped of its hyphens and prefixed with `o` for "object". The result is 42
 * characters and the transformation is total for every id this application mints, because
 * INV-7 makes every metadata id a uuid.
 *
 * PER OBJECT rather than one document holding a map of them, and that is a correctness
 * choice rather than a filing one: a PUT replaces a whole preference value, so two tabs on
 * two different objects saving one shared map would each write their own stale copy and
 * the second would forget the first one's list.
 */
export function lastViewKey(objectId: string): string {
  return `lastview.o${objectId.replaceAll('-', '')}`;
}

/**
 * WHERE THE MEMORY LIVES - the seam V4 keeps open.
 *
 * Three operations and one question, and no more: what a list reopens on is one value per
 * object, and an interface that also offered "list everything" or "clear all" would be
 * inviting a second caller that is not the list screen.
 *
 * `isReady` IS THE FLASH GUARD. The memory arrives over the network now, so a list whose
 * URL names no view does not know which view it is yet - and rendering the default view
 * first and swapping it a moment later reads as a bug rather than as a preference. It is
 * `usePanels`' own rule ("both reads gate the screen"), and the list holds its skeleton
 * until this is true. It becomes true on a FAILED read as well: a preference document
 * nobody could fetch means "nothing remembered", which is the behaviour this package
 * replaced and a perfectly good screen.
 *
 * `read` IS SYNCHRONOUS because its caller is an effect that decides, on the spot, whether
 * to rewrite the URL - so it reads the already-loaded document rather than fetching.
 * `write` and `clear` return nothing and are awaited by nobody: no navigation waits for a
 * preference to be saved.
 */
export interface LastViewStorage {
  /** Whether the memory can be read yet. A list with no view in its URL waits for this. */
  readonly isReady: boolean;
  /** The remembered choice: a saved view's id, `NO_VIEW`, or null for "nothing yet". */
  readonly read: (objectId: string) => string | null;
  /** Remembers a saved view's id, or `NO_VIEW` for the explicit unsaved list. */
  readonly write: (objectId: string, choice: string) => void;
  /** Forgets this object's view - what a remembered view that no longer exists gets (V3). */
  readonly clear: (objectId: string) => void;
}

/**
 * THE ONE BINDING (V4): the preference store, behind the interface.
 *
 * WRITES ARE SKIPPED WHEN THEY WOULD CHANGE NOTHING, and that is load-bearing rather than
 * an optimisation. `useSetPreference` updates the cached document optimistically, so a
 * write produces a new document, which produces a new storage object, which re-runs the
 * effect that called the write - and a write that fired again on the value it just stored
 * would be a loop of PUTs. Comparing first ends it at the source, and it also means
 * reopening the same list ten times is ten reads and no writes at all.
 *
 * CLEARING IS A PUT OF `null`, because the preference routes have no DELETE: one key, one
 * value, and a value that is not a string reads back as "nothing remembered" (V3).
 */
export function useLastViewStorage(): LastViewStorage {
  const preferences = usePreferences();
  const { mutate } = useSetPreference();
  const stored = preferences.data;
  const isReady = !preferences.isPending;

  return useMemo(
    () => ({
      isReady,
      read: (objectId: string) => {
        const value = stored?.[lastViewKey(objectId)];
        // Anything that is not a non-empty string is somebody's own PUT or a cleared key.
        return typeof value === 'string' && value !== '' ? value : null;
      },
      write: (objectId: string, choice: string) => {
        const key = lastViewKey(objectId);
        if (stored?.[key] === choice) return;
        mutate({ key, value: choice });
      },
      clear: (objectId: string) => {
        const key = lastViewKey(objectId);
        const current = stored?.[key];
        if (current === undefined || current === null) return;
        mutate({ key, value: null });
      },
    }),
    [isReady, stored, mutate],
  );
}
